import { mentionType } from '@aline/common';
import { PathApi, TextApi } from 'platejs';
import type { Path, PathRef, SlateEditor, TElement } from 'platejs';

import {
  getDocumentMentionDisplayName,
  getSelectedEntityKey,
} from './mention-entity-picker-helpers';
import type { MultiSelectSearchResult, SearchResult } from './mention-entity-picker-helpers';
import type {
  DocumentMentionElement,
  PlaybookMentionElement,
  ReportMentionElement,
  TMentionElement,
  UserMentionElement,
  WorkflowMentionElement,
} from '../../ChatEditor/ChatEditorMention/utils';
import { getIsMentionElement } from '../../ChatEditor/ChatEditorMention/utils';

/**
 * The chips checked in one open `@` picker, keyed like `getSelectedEntityKey`.
 *
 * A path ref follows each chip as the document changes around it. Only these chips are
 * toggled by the picker's checkboxes: a mention the user wrote into the message earlier is
 * part of their sentence, and unchecking a row must not reach back and delete it.
 */
export type SessionMentions = Map<string, PathRef>;

/**
 * Check or uncheck a document, playbook, workflow or report row while the picker stays open.
 *
 * Checking inserts the chip, and the space after it, just before the trigger, so the search
 * and the open menu stay where they are. Unchecking removes that same chip and its space.
 */
export function toggleSessionMention({
  editor,
  inputElement,
  result,
  sessionId,
  sessionMentions,
}: {
  editor: SlateEditor;
  inputElement: TElement;
  result: MultiSelectSearchResult;
  sessionMentions: SessionMentions;
  sessionId?: string;
}): void {
  const key = getSelectedEntityKey(result);
  const checked = sessionMentions.get(key);
  if (checked) {
    sessionMentions.delete(key);
    const path = checked.unref();
    if (path && getMentionKeyAt(editor, path) === key) removeMentionWithTrailingSpace(editor, path);
    return;
  }

  const inputPath = editor.api.findPath(inputElement);
  if (!inputPath) return;

  editor.tf.insertNodes(
    [{ ...getMentionNodeForResult(result), mentionPickerSessionId: sessionId }, { text: ' ' }],
    { at: inputPath }
  );
  sessionMentions.set(key, editor.api.pathRef(inputPath));
}

/** Restore only this picker's chips after Activity has released its path refs. */
export function restoreSessionMentions({
  editor,
  sessionId,
  sessionMentions,
}: {
  editor: SlateEditor;
  sessionId: string;
  sessionMentions: SessionMentions;
}): void {
  for (const [node, path] of editor.api.nodes({
    at: [],
    match: node => getIsMentionElement(node),
  })) {
    if (getIsMentionElement(node) && node.mentionPickerSessionId === sessionId) {
      const key = getMentionEntityKey(node);
      if (key && !sessionMentions.has(key)) sessionMentions.set(key, editor.api.pathRef(path));
    }
  }
}

/** The keys of the session's chips that are still in the document, for the checkboxes. */
export function getSessionMentionKeys(
  editor: SlateEditor,
  sessionMentions: SessionMentions
): Set<string> {
  const keys = new Set<string>();
  for (const [key, pathRef] of sessionMentions) {
    const path = pathRef.current;
    if (path && getMentionKeyAt(editor, path) === key) keys.add(key);
  }
  return keys;
}

/**
 * The key a mention chip is checked under. It must match `getSelectedEntityKey` for the
 * search result the chip was built from, or a checked row would read as unchecked.
 */
export function getMentionEntityKey(node: TMentionElement): string | null {
  switch (node.mentionTypeName) {
    case 'document':
      return `Document:${node.documentId}`;
    case 'playbook':
      return `Playbook:${node.playbookId}`;
    case 'workflow':
      return `Workflow:${node.workflowId}`;
    case 'report':
      return `Report:${node.reportId}`;
    default:
      return null;
  }
}

export function getMentionNodeForResult(result: SearchResult): TMentionElement {
  switch (result.__typename) {
    case 'Document':
      return getDocumentMentionElement({ id: result.id, name: result.name });
    case 'Playbook':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'playbook',
        playbookId: result.id,
        playbookName: result.title,
        type: mentionType,
        value: result.title,
      } satisfies PlaybookMentionElement;
    case 'Report':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'report',
        reportId: result.id,
        reportName: result.name,
        type: mentionType,
        value: result.name,
      } satisfies ReportMentionElement;
    case 'User':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'user',
        type: mentionType,
        userId: result.id,
        userName: result.displayName,
        value: result.displayName,
      } satisfies UserMentionElement;
    case 'Workflow':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'workflow',
        type: mentionType,
        value: result.name,
        workflowId: result.id,
        workflowName: result.name,
      } satisfies WorkflowMentionElement;
  }
}

/** The chip for a document, from a search result or a file the user just uploaded. */
export function getDocumentMentionElement(file: { id: string; name: string }) {
  const documentName = getDocumentMentionDisplayName(file.name);

  return {
    children: [{ text: '' }],
    documentId: file.id,
    documentName,
    mentionTypeName: 'document',
    type: mentionType,
    value: documentName,
  } satisfies DocumentMentionElement;
}

function getMentionKeyAt(editor: SlateEditor, path: Path): string | null {
  const node = editor.api.node(path)?.[0];
  return getIsMentionElement(node) ? getMentionEntityKey(node) : null;
}

function removeMentionWithTrailingSpace(editor: SlateEditor, path: Path): void {
  const nextPath = PathApi.next(path);
  const next = editor.api.node(nextPath)?.[0];

  editor.tf.withoutNormalizing(() => {
    if (TextApi.isText(next) && next.text.startsWith(' '))
      editor.tf.delete({
        at: { anchor: { offset: 0, path: nextPath }, focus: { offset: 1, path: nextPath } },
      });
    editor.tf.removeNodes({ at: path });
  });
}
